export const getUserDetails = () => {
  try {
    return JSON.parse(localStorage.getItem("userDetail") || "{}");
  } catch {
    return {};
  }
};

export const resolveSchoolId = (userDetails) => {
  const id =
    userDetails?.schooleId?._id ||
    userDetails?.schooleId ||
    userDetails?.schoolId?._id ||
    userDetails?.schoolId;
  return id != null ? String(id) : "";
};

export const isSuperAdminRole = (userDetails) =>
  String(userDetails?.role || "").toUpperCase() === "SUPERADMIN";

export const isSchooleRole = (userDetails) =>
  String(userDetails?.role || "").toUpperCase() === "SCHOOLE";

export const canUseActivityCatalog = (userDetails) =>
  isSuperAdminRole(userDetails) || isSchooleRole(userDetails);

export const normalizeId = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
};

export const resolveSchoolLabel = (school) => {
  if (!school) return "School";
  if (typeof school === "string") return school;
  return (
    school.schooleName ||
    school.schoolName ||
    school.orgName ||
    school.name ||
    school.schoolAutoGenerateId ||
    school.email ||
    normalizeId(school._id) ||
    "School"
  );
};

export const resolveClassLabel = (cls, includeSchool = false) => {
  const className = cls?.className || "—";
  if (!includeSchool) return className;
  const schoolLabel = resolveSchoolLabel(cls?.schooleId);
  return schoolLabel && schoolLabel !== "School"
    ? `${className} (${schoolLabel})`
    : className;
};

/**
 * List API path segment + role query for /list/:schooleId endpoints.
 * SUPERADMIN with no filterSchoolId uses "all" (cross-tenant read).
 */
export const buildListSchoolRequest = (userDetails, filterSchoolId) => {
  const role = String(userDetails?.role || "").toUpperCase();
  if (role === "SUPERADMIN") {
    const listId = filterSchoolId ? String(filterSchoolId) : "all";
    return { listId, role: "SUPERADMIN" };
  }
  if (filterSchoolId) {
    return { listId: String(filterSchoolId), role: role || undefined };
  }
  return { listId: resolveSchoolId(userDetails), role: role || undefined };
};

export const appendRoleQuery = (url, role) => {
  if (!role) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}role=${encodeURIComponent(role)}`;
};

/** schooleId for create/update when superadmin picks a school in the catalog filter */
export const effectiveSchoolIdForWrite = (userDetails, filterSchoolId) => {
  if (filterSchoolId) {
    return String(filterSchoolId);
  }
  if (isSuperAdminRole(userDetails)) {
    return "";
  }
  return resolveSchoolId(userDetails);
};

export const resolveRowSchoolId = (row) => normalizeId(row?.schooleId);

/** School name for rows created under a specific school; null = system/default row */
export const resolveRowSchoolLabel = (row, classList = []) => {
  if (row?.schooleId && typeof row.schooleId === "object") {
    return resolveSchoolLabel(row.schooleId);
  }
  const sid = resolveRowSchoolId(row);
  if (!sid) return null;

  const classRef = row?.classId;
  if (classRef?.schooleId && typeof classRef.schooleId === "object") {
    return resolveSchoolLabel(classRef.schooleId);
  }

  const classId = normalizeId(classRef);
  const cls = (classList || []).find((c) => normalizeId(c._id) === classId);
  if (cls?.schooleId) return resolveSchoolLabel(cls.schooleId);

  return null;
};

export const isSchoolAddedRow = (row) => Boolean(resolveRowSchoolId(row));

/** Show School column in Activity Catalog tables (superadmin cross-school view) */
export const shouldShowSchoolColumn = (embedded, scope) =>
  Boolean(embedded && scope?.isSuperAdmin);

export const resolveSchoolIdForClass = (classList, classId, filterSchoolId, userDetails) => {
  if (filterSchoolId) return String(filterSchoolId);
  const cls = (classList || []).find((c) => normalizeId(c._id) === String(classId));
  const fromClass = normalizeId(cls?.schooleId);
  if (fromClass) return fromClass;
  return resolveSchoolId(userDetails);
};

/** Include global (null schooleId) rows plus rows for the selected school */
export const rowMatchesSchoolScope = (row, filterSchoolId) => {
  if (!filterSchoolId) return true;
  const rowSid = resolveRowSchoolId(row);
  if (!rowSid) return true;
  return rowSid === String(filterSchoolId);
};

export const filterRowsBySchoolScope = (rows, filterSchoolId) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!filterSchoolId) return list;
  return list.filter((row) => rowMatchesSchoolScope(row, filterSchoolId));
};

export const buildScopedListBody = (listId, fields = {}) => {
  const body = { ...fields };
  if (listId && listId !== "all") {
    body.schooleId = String(listId);
  }
  return body;
};

export const dedupeSuperActivitiesForFilter = (rows) => {
  const byKey = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const name = row?.superActivityName || "";
    const key = name ? name.toLowerCase() : String(row._id);
    if (!byKey.has(key)) byKey.set(key, row);
  });
  return Array.from(byKey.values());
};

export const filterRowsBySuperActivity = (rows, superActivityId, areaOptions = []) => {
  if (!superActivityId) return Array.isArray(rows) ? rows : [];
  const fsa = String(superActivityId);
  const selected = (areaOptions || []).find((a) => String(a._id) === fsa);
  const areaName = selected?.superActivityName;
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const sa = row?.superActivityId;
    const id = normalizeId(sa);
    if (id && id === fsa) return true;
    const name = sa?.superActivityName;
    if (areaName && name && String(name).toLowerCase() === String(areaName).toLowerCase()) {
      return true;
    }
    return false;
  });
};

export const filterRowsByClassId = (rows, classId) => {
  if (!classId) return Array.isArray(rows) ? rows : [];
  const cid = String(classId);
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const rc = row?.classId;
    const id = rc?._id != null ? rc._id : rc;
    return id != null && String(id) === cid;
  });
};

export const ACTIVITY_CATALOG_TABS = [
  {
    key: "areas",
    label: "Area Of Work",
    shortLabel: "Areas",
    description: "Top-level curriculum domains",
    step: 1,
  },
  {
    key: "activities",
    label: "List Of Activities",
    shortLabel: "Activities",
    description: "Activities within each area",
    step: 2,
  },
  {
    key: "exercises",
    label: "Exercise",
    shortLabel: "Exercises",
    description: "Individual exercises & drills",
    step: 3,
  },
];

export const tabIndexFromQuery = (search) => {
  const key = new URLSearchParams(search).get("tab");
  const idx = ACTIVITY_CATALOG_TABS.findIndex((t) => t.key === key);
  return idx >= 0 ? idx : 0;
};
